<script>
// import RexModal from '@/rex-components/rex-modal.vue';
import RexModalV3 from '@/rex-components/RexModalV3.vue';
// import RexButton from '@/rex-components/rex-button.vue';
import RexModalLabel from '@/rex-components/rex-modal-label.vue';
import RexModalInput from '@/rex-components/rex-modal-input.vue';
import LogQueryEditor from '@/components/Log/LogQueryEditor.vue';
import RexInfoLabel from '@/rex-components/rex-info-label.vue';
import Loading from '@/rex-components/rex-loading.vue';
import { mapGetters, mapMutations, mapState } from 'vuex';
import {
  METRIC_QUERY_TYPE,
  NAME_FIELD_REGEX,
  TABLE_QUERY_TYPE,
  DEBOUNCE_INPUT_TIME,
} from '@/components/Log/log.constants';
import { LOGService } from '@/services/api/log.service';
import { getTimeZone } from '@/utils/time/timezone';
import { dateFormat } from '@/store/modules/global/settings/settings.getter';
import { TimeUnits } from '@/utils/enums/timeUnits';
import debounce from 'lodash/debounce';

export default {
  emits: ['onSubmit', 'setLoading', 'onClose'],
  components: {
    // RexModal,
    RexModalV3,
    // RexButton,
    RexModalLabel,
    RexModalInput,
    Loading,
    LogQueryEditor,
    RexInfoLabel,
  },
  computed: {
    ...mapState('globalState/user', ['teamID']),
    ...mapState('globalState/settings', ['timezoneoffset']),
    ...mapGetters('logState/logCommonState', ['getCapabilityType']),
    getQueryFieldBoxCss() {
      return (
        this.queryValidationMsg &&
        (this.isQueryValid
          ? 'add-widget-query-field-success'
          : 'add-widget-query-field-err')
      );
    },
    getValidateBtnVariantClass() {
      return this.isQueryValid
        ? 'query-validate-btn-disabled'
        : 'query-validate-btn-secondary';
    },
    getLoadingBtnClass() {
      return this.validateLoading ? 'query-validate-btn-disabled' : '';
    },
    getFormTitle() {
      return this.isEdit
        ? this.$t('log.editSavedSearch')
        : this.$t('log.createSavedSearch');
    },
    getSubmitText() {
      return this.isEdit ? this.$t('log.update') : this.$t('log.create');
    },
  },
  props: {
    initValues: {
      type: Object,
      default: () => ({}),
    },
    isEdit: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      searchName: this.initValues.searchName || '',
      queryStr: this.initValues.queryStr || '',
      searchNameErrMsg: '',
      queryStrErrMsg: '',
      queryValidationMsg: '',
      isQueryValid: this.initValues.queryStr?.length > 0,
      validateLoading: false,
      isNameCheckLoading: false,
    };
  },
  methods: {
    ...mapMutations('globalState/app', ['setIsFormOpened']),
    validateQueryType(queryType) {
      if (this.isEdit) {
        return queryType === this.initValues.savedQueryType;
      } else {
        return (
          queryType === METRIC_QUERY_TYPE || queryType === TABLE_QUERY_TYPE
        );
      }
    },
    isQueryEmpty() {
      if (this.queryStr?.length === 0) {
        this.validateLoading = false;
        this.queryValidationMsg = this.$t('log.enterValidQuery');
        return true;
      }
      return false;
    },
    async validateNameExists() {
      try {
        if (this.isEdit) return;
        this.isNameCheckLoading = true;

        const { data } = await LOGService.checkSavedSearchNameExists({
          metric_name: this.searchName,
          team_id: this.teamID,
          capability: this.getCapabilityType,
        });
        this.searchNameErrMsg =
          data.success && data.data?.id
            ? this.$t('log.savedSearchNameAlreadyExists')
            : '';
        this.isNameCheckLoading = false;
      } catch (error) {
        console.log('error', error);
        this.searchNameErrMsg =
          error.status === 400
            ? error.message
            : this.$t('log.savedSearchNameAlreadyExistsErrMsg');
        this.isNameCheckLoading = false;
      }
    },
    debouncedValidateNameExists: debounce(function () {
      this.validateNameExists();
    }, DEBOUNCE_INPUT_TIME),
    onSavedSearchNameChange(value) {
      if (this.searchNameErrMsg) this.searchNameErrMsg = '';
      this.searchName = value;
      this.debouncedValidateNameExists();
    },
    async validateQuery() {
      try {
        if (this.isQueryEmpty()) return;
        const date = new Date();
        const startDate = new Date(new Date().setHours(date.getHours() - 1));
        const endDate = new Date(new Date().setHours(date.getHours() + 1));
        this.validateLoading = true;
        const { data } = await LOGService.applySqlQuery({
          query: this.queryStr,
          limit: 3,
          offset: 0,
          team_id: this.teamID,
          timeZone: getTimeZone(this.timezoneoffset),
          duration: {
            start: dateFormat(startDate, TimeUnits.HOUR),
            end: dateFormat(endDate, TimeUnits.HOUR),
            step: TimeUnits.HOUR,
          },
          capability: this.getCapabilityType,
        });
        if (data.success) {
          this.isQueryValid = this.validateQueryType(data.data.savedQueryType);
          this.queryValidationMsg = !this.isQueryValid
            ? this.$t('log.queryShouldReturnType', [
                this.initValues.savedQueryType === TABLE_QUERY_TYPE
                  ? 'table'
                  : 'metric',
              ])
            : this.$t('log.queryIsValid');
        } else {
          this.queryValidationMsg = this.$t('log.errorWhileValidatingQuery');
          this.isQueryValid = false;
        }
        this.validateLoading = false;
      } catch (error) {
        console.log('error', error);
        this.queryValidationMsg =
          error.status === 400
            ? error?.data?.errors?.message ??
              this.$t('log.querySyntaxNotSupported')
            : this.$t('log.errorWhileValidatingQuery');
        this.isQueryValid = false;
        this.validateLoading = false;
      }
    },
    validateForm() {
      const regex = new RegExp(NAME_FIELD_REGEX);

      this.searchNameErrMsg =
        (!regex.test(this.searchName) ||
          !this.searchName?.trim() ||
          this.searchName?.length > 50) &&
        this.$t('log.enterValidSavedSearchName');

      this.queryStrErrMsg =
        !this.queryStr?.trim() && this.$t('log.enterValidQuery');

      return !this.searchNameErrMsg && !this.queryStrErrMsg;
    },
    onSubmitForm() {
      if (this.validateForm()) {
        this.$emit('onSubmit', {
          searchName: this.searchName,
          query: this.queryStr,
        });
      }
    },
    resetErrData() {
      this.isQueryValid = false;
      this.queryValidationMsg = '';
    },
  },
  unmounted() {
    this.setIsFormOpened(false);
  },
};
</script>

<template>
  <div>
    <RexModalV3
      :show="true"
      @onClose="() => $emit('onClose')"
      :title="getFormTitle"
      @onSubmit="onSubmitForm"
      :loading="isLoading"
      :submitText="getSubmitText"
      :cancelButtonProps="{ id: 'log-saved-search-form-cancel' }"
      :submitBtnProps="{
        disabled: !isQueryValid || !!searchNameErrMsg,
        loading: isLoading,
        id: 'log-saved-search-form-submit',
      }"
    >
      <template #body>
        <div class="saved-search-form-body">
          <div class="saved-search-input-fields">
            <RexModalLabel
              :primaryLabel="$t('log.savedSearchName')"
              secondaryLabel="*"
              labelForId="log-saved-search-form-name"
            />
            <RexModalInput
              style="height: 2.45rem"
              type="text"
              :placeholder="$t('log.enterName')"
              :modelValue="searchName"
              @update:modelValue="onSavedSearchNameChange"
              id="log-saved-search-form-name"
              :maxLength="50"
              class="saved-search-name-field"
              :disabled="isEdit"
            />
            <RexInfoLabel
              v-if="!!searchNameErrMsg"
              :isError="true"
              :info="searchNameErrMsg"
            />
            <div v-if="isNameCheckLoading" class="name-check-loader-box">
              <div>
                <Loading :smallar="true" class="name-check-loader" />
              </div>
              <div class="name-check-label">
                {{ $t('log.validateUniqueness') }}
              </div>
            </div>
          </div>

          <div>
            <RexModalLabel
              :primaryLabel="$t('log.query')"
              secondaryLabel="*"
              labelForId="log-saved-search-form-query"
            />
            <div class="add-widget-query-field" :class="getQueryFieldBoxCss">
              <LogQueryEditor
                id="log-saved-search-form-query"
                :editorValue="queryStr"
                @onTextChange="
                  (val) => {
                    queryStr = val;
                    resetErrData();
                  }
                "
                :styleObj="{ height: '18rem' }"
              />
              <div class="add-widget-query-actions">
                <div>
                  <div
                    class="query-validate-err-msg"
                    v-if="!!queryValidationMsg"
                  >
                    <BaseIcon
                      :name="isQueryValid ? 'verified' : 'error'"
                      height="1rem"
                      width="1rem"
                    />
                    <span
                      :class="isQueryValid ? 'query-success' : 'query-failure'"
                    >
                      {{ queryValidationMsg }}
                    </span>
                  </div>
                </div>
                <button
                  id="add-widget-query-validate"
                  @click="validateQuery"
                  class="query-validate-btn"
                  :class="`${getValidateBtnVariantClass} ${getLoadingBtnClass}`"
                >
                  <span v-if="!validateLoading">
                    {{ $t('log.validate') }}
                  </span>
                  <Loading v-else :smallar="true" class="validate-loader" />
                </button>
              </div>
            </div>
          </div>
        </div>
      </template>
    </RexModalV3>
  </div>
</template>

<style lang="scss" scoped>
.saved-search-input-fields {
  margin-bottom: 1.75rem;
}
.log-query-note {
  color: $stepper-disabled-grey;
  font-size: 0.875rem;
}
.saved-search-name-field {
  margin-bottom: 0.625rem;
}
.saved-search-query-field {
  margin-bottom: 0.625rem;
  border: 0.063rem solid $border-primary;
  border-radius: 0.5rem;
  padding: 1rem 1rem 1rem 0.2rem;
  background-color: rgb(26, 26, 26);
}
.saved-search-query-field-err {
  border-color: $poor;
}
.log-query-label-box {
  @include flex-center-justify(space-between);
}
.log-query-note {
  color: $stepper-disabled-grey;
  font-size: 0.875rem;
}
.add-widget-query-field {
  margin-bottom: 0.625rem;
  border: 0.063rem solid $border-primary;
  border-radius: 0.5rem;
  padding: 1rem 1rem 1rem 0.2rem;
  background-color: rgb(26, 26, 26);
}
.add-widget-query-field-err {
  border-color: $poor;
}
.add-widget-query-field-success {
  border-color: $good;
}
.add-widget-query-actions {
  @include flex-center-justify(space-between);
  margin-top: 1rem;
}
.query-validate-err-msg {
  @include flex-center-vert;
  margin-left: 1rem;
}
.query-success {
  padding: 0.05rem 0rem 0rem 0.5rem;
  border-color: $good;
  color: $good;
}
.query-failure {
  padding: 0.15rem 0rem 0rem 0.5rem;
  border-color: $poor;
  color: $poor;
  display: inline-block;
  max-height: 4rem;
  overflow-y: auto;
}
.query-validate-btn {
  background-color: inherit;
  margin-top: 0.25rem;
  border: none;
  font-size: 1rem;
}
.query-validate-btn-secondary {
  color: $color-brand-accent;
}
.query-validate-btn-disabled {
  color: $button-disabled-light;
  pointer-events: none;
  cursor: not-allowed !important;
}
.footer-container {
  @include flex-center-justify(space-between);
  border-top: 0.063rem solid $border-primary;
  padding-top: 1.5rem;
}
.cancel-btn {
  @include dropshadow-effect($opacity: 0);
  border: none;
  padding: 0;
  margin-right: 1.75rem;
  font-size: 1rem;
}
.submit-btn {
  font-size: 1rem;
  padding: 0 1rem 0 1rem;
}
.saved-search-info-label {
  margin-top: 0.625rem;
}
.saved-search-form-err-msg {
  color: $poor;
  font-size: 1rem;
}
.validate-loader {
  padding-right: 1rem;
}
.name-check-loader-box {
  @include flex-center-justify(flex-start);
}
.name-check-loader {
  transform: scale(0.7);
}
.name-check-label {
  margin-left: 0.2rem;
  color: $font-tertiary;
}
</style>
